/**
 * Panel Colaboradores (landing_partners): listado + crear/editar.
 * Extensible: Alta de nuevos partners (Marnific, Turismo Marinero, …).
 */

import { useEffect, useMemo, useState } from 'react';
import { Handshake, Loader2, Plus, RefreshCw, Save, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/notify';
import { storeStatusLabel } from '@/lib/store-status';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    useLandingPartnersList,
    useSetLandingPartnerStatus,
    useUpsertLandingPartner,
} from './hooks/useStorePropios';
import type {
    LandingPartnerRow,
    LandingPartnerStatus,
    LandingPartnerType,
} from './types';

function highlightsToText(raw: unknown): string {
    if (!raw) return '';
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x)).join('\n');
    }
    return '';
}

function textToHighlights(raw: string): string[] {
    return raw
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
}

function slugsToText(arr: string[] | null | undefined): string {
    return (arr ?? []).join(', ');
}

function textToSlugs(raw: string): string[] {
    return raw
        .split(/[,\n]/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
}

function emptyForm() {
    return {
        slug: '',
        name: '',
        partnerType: 'marca' as LandingPartnerType,
        description: '',
        logoUrl: '',
        coverImageUrl: '',
        websiteUrl: '',
        articleUrl: '',
        countryCode: 'ES',
        sortOrder: '0',
        status: 'draft' as LandingPartnerStatus,
        tagline: '',
        highlights: '',
        sponsorClubSlugs: '',
        sponsorEventSlugs: '',
        isFeatured: false,
    };
}

type FormState = ReturnType<typeof emptyForm>;

function PartnerLogo({ src, name }: { src: string | null; name: string }) {
    if (!src) {
        return (
            <div className="h-10 w-10 rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center text-muted-foreground">
                <Handshake size={14} />
            </div>
        );
    }
    return (
        <div className="h-10 w-10 rounded-lg border border-border overflow-hidden bg-card flex items-center justify-center">
            <img src={src} alt={name} className="max-h-9 max-w-9 object-contain" />
        </div>
    );
}

export function PartnersPanel() {
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);

    const { data, isLoading, isError, error, refetch, isFetching } = useLandingPartnersList(
        statusFilter,
        search,
    );
    const upsert = useUpsertLandingPartner();
    const setStatus = useSetLandingPartnerStatus();

    const rows = data ?? [];
    const selected = useMemo(
        () => rows.find((r) => r.id === selectedId) ?? null,
        [rows, selectedId],
    );

    useEffect(() => {
        if (creating) return;
        if (!selected) {
            setForm(emptyForm());
            return;
        }
        setForm({
            slug: selected.slug,
            name: selected.name,
            partnerType: (selected.partner_type as LandingPartnerType) || 'marca',
            description: selected.description ?? '',
            logoUrl: selected.logo_url ?? '',
            coverImageUrl: selected.cover_image_url ?? '',
            websiteUrl: selected.website_url ?? '',
            articleUrl: selected.article_url ?? '',
            countryCode: selected.country_code ?? 'ES',
            sortOrder: String(selected.sort_order ?? 0),
            status: (selected.status as LandingPartnerStatus) || 'draft',
            tagline: selected.tagline ?? '',
            highlights: highlightsToText(selected.highlights),
            sponsorClubSlugs: slugsToText(selected.sponsor_club_slugs),
            sponsorEventSlugs: slugsToText(selected.sponsor_event_slugs),
            isFeatured: Boolean(selected.is_featured),
        });
    }, [selected, creating]);

    const startCreate = () => {
        setCreating(true);
        setSelectedId(null);
        setForm(emptyForm());
    };

    const onSelect = (row: LandingPartnerRow) => {
        setCreating(false);
        setSelectedId(row.id);
    };

    const onSave = async () => {
        if (!form.name.trim()) {
            notify({ type: 'err', text: 'El nombre es obligatorio' });
            return;
        }
        if (creating && !form.slug.trim()) {
            notify({ type: 'err', text: 'El slug es obligatorio al crear' });
            return;
        }
        try {
            const id = await upsert.mutateAsync({
                id: creating ? null : selectedId,
                slug: form.slug.trim().toLowerCase(),
                name: form.name.trim(),
                partnerType: form.partnerType,
                description: form.description.trim() || null,
                logoUrl: form.logoUrl.trim() || null,
                coverImageUrl: form.coverImageUrl.trim() || null,
                websiteUrl: form.websiteUrl.trim() || null,
                articleUrl: form.articleUrl.trim() || null,
                countryCode: form.countryCode.trim() || null,
                sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
                status: form.status,
                tagline: form.tagline.trim() || null,
                highlights: textToHighlights(form.highlights),
                sponsorClubSlugs: textToSlugs(form.sponsorClubSlugs),
                sponsorEventSlugs: textToSlugs(form.sponsorEventSlugs),
                isFeatured: form.isFeatured,
            });
            notify({ type: 'ok', text: creating ? 'Colaborador creado' : 'Colaborador guardado' });
            setCreating(false);
            if (typeof id === 'string') setSelectedId(id);
            await refetch();
        } catch (e: unknown) {
            notify({ type: 'err', text: e instanceof Error ? e.message : String(e) });
        }
    };

    const onQuickStatus = async (row: LandingPartnerRow, next: LandingPartnerStatus) => {
        try {
            await setStatus.mutateAsync({ id: row.id, status: next });
            notify({ type: 'ok', text: `Estado: ${storeStatusLabel(next)}` });
        } catch (e: unknown) {
            notify({ type: 'err', text: e instanceof Error ? e.message : String(e) });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                <Loader2 className="animate-spin" size={16} />
                Cargando colaboradores…
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
                {(error as Error)?.message || 'Error al cargar colaboradores'}
            </div>
        );
    }

    const editing = creating || Boolean(selected);

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                <div className="flex flex-1 flex-wrap gap-2 items-center">
                    <div className="relative flex-1 max-w-sm min-w-[180px]">
                        <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                        <Input
                            className="pl-8 h-9"
                            placeholder="Buscar colaborador…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Todos</option>
                        <option value="published">Publicados</option>
                        <option value="draft">Borrador</option>
                        <option value="archived">Archivados</option>
                    </select>
                </div>
                <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={startCreate}>
                        <Plus size={14} />
                        Nuevo colaborador
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Actualizar
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
                <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                                <th className="text-left font-medium px-3 py-2">Colaborador</th>
                                <th className="text-left font-medium px-3 py-2">Tipo</th>
                                <th className="text-left font-medium px-3 py-2">Estado</th>
                                <th className="text-left font-medium px-3 py-2">Destacado</th>
                                <th className="text-right font-medium px-3 py-2">Orden</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.id}
                                    className={cn(
                                        'border-t border-border/60 cursor-pointer hover:bg-muted/30',
                                        !creating && selectedId === r.id && 'bg-primary/5',
                                    )}
                                    onClick={() => onSelect(r)}
                                >
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <PartnerLogo src={r.logo_url} name={r.name} />
                                            <div>
                                                <div className="font-medium text-foreground">{r.name}</div>
                                                <div className="text-xs text-muted-foreground">{r.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.partner_type}</td>
                                    <td className="px-3 py-2">
                                        <StatusBadge status={r.status} label={storeStatusLabel(r.status)} size="sm" />
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.is_featured ? (
                                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-sky-500/15 text-sky-700 dark:text-sky-300">
                                                sí
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-muted-foreground">
                                        {r.sort_order}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                                        Sin colaboradores. Crea el primero con «Nuevo colaborador».
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="rounded-lg border border-border p-4 space-y-3 h-fit sticky top-4">
                    <h3 className="font-semibold text-foreground">
                        {creating ? 'Nuevo colaborador' : selected ? 'Editar colaborador' : 'Detalle'}
                    </h3>
                    {!editing ? (
                        <p className="text-sm text-muted-foreground">
                            Selecciona un colaborador o crea uno nuevo (p. ej. Marnific, Turismo Marinero).
                        </p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                    <Label>Slug</Label>
                                    <Input
                                        value={form.slug}
                                        disabled={!creating}
                                        onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                                        placeholder="mi-colaborador"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                    <Label>Estado</Label>
                                    <select
                                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                        value={form.status}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                status: e.target.value as LandingPartnerStatus,
                                            }))
                                        }
                                    >
                                        <option value="draft">{storeStatusLabel('draft')}</option>
                                        <option value="published">{storeStatusLabel('published')}</option>
                                        <option value="archived">{storeStatusLabel('archived')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label>Nombre (tile Tienda)</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Tagline</Label>
                                <Input
                                    value={form.tagline}
                                    onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Descripción</Label>
                                <textarea
                                    className="w-full min-h-[90px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, description: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label>Tipo</Label>
                                    <select
                                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                        value={form.partnerType}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                partnerType: e.target.value as LandingPartnerType,
                                            }))
                                        }
                                    >
                                        <option value="marca">marca</option>
                                        <option value="tienda">tienda</option>
                                        <option value="federacion">federacion</option>
                                        <option value="otro">otro</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <Label>País</Label>
                                    <Input
                                        value={form.countryCode}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, countryCode: e.target.value }))
                                        }
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label>Orden</Label>
                                    <Input
                                        value={form.sortOrder}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, sortOrder: e.target.value }))
                                        }
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Web</Label>
                                    <Input
                                        value={form.websiteUrl}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, websiteUrl: e.target.value }))
                                        }
                                    />
                                </div>
                            </div>
                            <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={form.isFeatured}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, isFeatured: e.target.checked }))
                                    }
                                />
                                <span className="text-sm">
                                    <span className="font-medium text-foreground">Destacado en Tienda</span>
                                    <span className="block text-xs text-muted-foreground">
                                        Se inserta en el feed Todo (posiciones 4, 9, 14…) de forma aleatoria entre
                                        los marcados. Solo colaboradores publicados.
                                    </span>
                                </span>
                            </label>
                            <div className="space-y-1">
                                <Label>Logo URL</Label>
                                <Input
                                    value={form.logoUrl}
                                    onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Cover URL</Label>
                                <Input
                                    value={form.coverImageUrl}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, coverImageUrl: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Artículo blog (URL)</Label>
                                <Input
                                    value={form.articleUrl}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, articleUrl: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Highlights (uno por línea)</Label>
                                <textarea
                                    className="w-full min-h-[70px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={form.highlights}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, highlights: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Clubes patrocinados (slugs, coma)</Label>
                                <Input
                                    value={form.sponsorClubSlugs}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, sponsorClubSlugs: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Eventos patrocinados (slugs, coma)</Label>
                                <Input
                                    value={form.sponsorEventSlugs}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, sponsorEventSlugs: e.target.value }))
                                    }
                                />
                            </div>

                            <div className="flex flex-wrap gap-2 pt-1">
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => void onSave()}
                                    disabled={upsert.isPending}
                                >
                                    {upsert.isPending
                                        ? <Loader2 size={14} className="animate-spin" />
                                        : <Save size={14} />}
                                    Guardar
                                </Button>
                                {selected && !creating && (
                                    <>
                                        {selected.status !== 'published' && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => void onQuickStatus(selected, 'published')}
                                            >
                                                Publicar
                                            </Button>
                                        )}
                                        {selected.status !== 'archived' && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => void onQuickStatus(selected, 'archived')}
                                            >
                                                Archivar
                                            </Button>
                                        )}
                                    </>
                                )}
                                {creating && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            setCreating(false);
                                            setForm(emptyForm());
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
